// fetches and returns notification data.
// e.g. { EMAIL: { ROOTSTECH_NEWS: { lastModified: "2020-08-04T19:07:40Z", subscribed: false } } }
import { useState } from 'react'
import axios from '@fs/zion-axios'
import { merge, set } from 'lodash-es'
import useErrorReporter from './useErrorReporter'
import useFetchData from './useFetchData'

// deep merge into new object
const mergeNotifications = (...sources) => merge({}, ...sources)

const setUnsubscribedAll = (notifications, unsubscribed) => {
  const value = unsubscribed ? formatISO(new Date()) : false
  set(notifications, 'blocked', value)
  set(notifications, 'EMAIL.blocked', value)
}

const fetchNotifications = async () => {
  const { data } = await axios.get('/service/messaging/preferences/preferences/categories')
  return data
}

async function updateNotificationData(ids, subscribed) {
  // setting notifications can be slow on the backend so avoid sending data if possible
  if (!ids.length) {
    return null
  }
  const lastModified = formatISO(new Date())
  const data = {}
  ids.forEach((id) => {
    set(data, id, {
      subscribed,
      lastModified,
    })
  })
  return axios.post('/service/messaging/preferences/preferences/categories', data)
}

const unblockNotifications = () => axios.delete('/service/messaging/preferences/preferences/block')

const blockNotifications = () => axios.post('/service/messaging/preferences/preferences/block')

export default function useNotifications() {
  const { data: remoteNotifications, loading: notificationsLoading } = useFetchData(fetchNotifications, {
    initialData: {},
  })
  const [localNotifications, setLocalNotifications] = useState({})
  const reportError = useErrorReporter()

  const notifications = mergeNotifications(remoteNotifications, localNotifications)
  const unsubscribed = !!notifications?.blocked
  const unsubscribedEmail = !!notifications?.EMAIL?.blocked

  const updateLocalNotificationState = ({ id, checked }) => {
    setLocalNotifications((prevState) => {
      const newLocalNotifications = mergeNotifications(prevState)
      set(newLocalNotifications, `${id}.subscribed`, checked)
      return newLocalNotifications
    })
  }

  const handleChangeNotification = async ({ id, checked }) => {
    updateLocalNotificationState({ id, checked })
    try {
      await updateNotificationData([id], checked)
    } catch (error) {
      updateLocalNotificationState({ id, checked: !checked })
      reportError(error)
    }
  }

  const updateLocalUnsubscribedState = (unsubscribe) => {
    setLocalNotifications((prevState) => {
      const newLocalNotifications = mergeNotifications(prevState)
      setUnsubscribedAll(newLocalNotifications, unsubscribe)
      return newLocalNotifications
    })
  }

  const toggleLocalUnsubscribedState = async (unsubscribe) => {
    updateLocalUnsubscribedState(unsubscribe)
    try {
      await (unsubscribe ? blockNotifications() : unblockNotifications())
    } catch (error) {
      updateLocalUnsubscribedState(!unsubscribe)
      reportError(error)
    }
  }

  const handleUnsubscribeAll = async () => toggleLocalUnsubscribedState(true)

  const handleRestoreAll = async () => toggleLocalUnsubscribedState(false)

  return {
    notifications,
    notificationsLoading,
    handleChangeNotification,
    unsubscribed,
    unsubscribedEmail,
    handleUnsubscribeAll,
    handleRestoreAll,
  }
}

function formatISO(date) {
  const iso = date.toISOString()
  return `${iso.substring(0, iso.indexOf('.'))}Z`
}
